import React, { useCallback } from 'react';

import Select from 'react-select';
import { OptionProps } from 'react-select/src/components/Option';

import Paper from '@material-ui/core/Paper';

import {
  ChipSelectProps,
  Control,
  MultiValue,
  Option,
  NoOptionsMessage,
  Placeholder,
  SingleValue,
  ValueContainer,
  chipSelectStyles,
  ClearIndicator,
  DropdownIndicator,
} from '../../ui/ui-chip-select';

import { RECIPIENT_TYPE, RecipientType } from './new-message-composer';

type RecipientTypeProps = {
  recipientTypeHandler: (type: RecipientType) => void;
  recipientType: RecipientType;
  hideRecipientToggle?: boolean;
};
type Props = ChipSelectProps &
  RecipientTypeProps & {
    isMulti: boolean;
    isClearable: boolean;
  };

const MenuWithRecipientToggle = (props: OptionProps<any> & RecipientTypeProps) => {
  const { recipientType, recipientTypeHandler, hideRecipientToggle } = props;
  const recipientSelectorText =
    recipientType === RECIPIENT_TYPE.EMPLOYEE
      ? 'Create group announcement'
      : 'Message individual employee(s)';

  const handleChangeRecipientClick = () =>
    recipientType === RECIPIENT_TYPE.EMPLOYEE
      ? recipientTypeHandler(RECIPIENT_TYPE.GROUP)
      : recipientTypeHandler(RECIPIENT_TYPE.EMPLOYEE);

  return (
    <Paper
      square
      className="u-position-absolute u-zIndex-1 u-margin-top"
      style={{ left: 0, right: 0 }}
      {...props.innerProps}
    >
      {props.children}
      {!hideRecipientToggle && (
        <div
          className="u-border-top u-padding-y u-padding-x-lg u-margin-x-xs typo-link-v2"
          role="presentation"
          onClick={handleChangeRecipientClick}
          data-cy="recipient-type-toggle"
        >
          {recipientSelectorText}
        </div>
      )}
    </Paper>
  );
};

export const NewMessageRecipientSelector = (inputProps: Props) => {
  const {
    options,
    value,
    onChange,
    label,
    placeholder,
    autofocus,
    isMulti,
    isClearable,
    recipientType,
    recipientTypeHandler,
    hideRecipientToggle,
  } = inputProps;

  const CustomMenu = useCallback(
    (props: OptionProps<any>) => (
      <MenuWithRecipientToggle
        recipientType={recipientType}
        recipientTypeHandler={recipientTypeHandler}
        hideRecipientToggle={hideRecipientToggle}
        {...props}
      />
    ),
    [recipientType, recipientTypeHandler, hideRecipientToggle],
  );

  const components: any = {
    Control,
    Menu: CustomMenu,
    MultiValue,
    NoOptionsMessage,
    Option,
    Placeholder,
    SingleValue,
    ValueContainer,
    ClearIndicator,
    DropdownIndicator,
  };

  return (
    <div className="u-flexGrow-1" data-cy="recipient-selector">
      <Select
        styles={chipSelectStyles}
        textFieldProps={{
          label,
          InputLabelProps: {
            shrink: true,
          },
        }}
        options={options}
        components={components}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        isMulti={isMulti}
        isClearable={isClearable}
        ref={(input) => {
          if (input && autofocus) {
            input.focus();
          }
        }}
      />
    </div>
  );
};
